import { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Phone, X, Check, Search, Copy, Info, UserCheck, UserX, MapPin } from 'lucide-react';
import { useSortable, SortTh } from '../hooks/useSortable';
import { getTeamsUsers, syncTeamsUsers, assignTeamsNumber, getZoomUsers, syncZoomUsers, assignZoomNumber, listDids, listVoiceRoutingPolicies, setTeamsUserVrp, pollJob } from '../services/portalApi';

const PLATFORMS = {
  teams: {
    label: 'Microsoft Teams',
    abbr:  'MT',
    bg:    '#0d2b4e',
    color: 'var(--info)',
    badge: { background: 'var(--info-bg)', color: 'var(--info)', border: '1px solid var(--info-border)' },
  },
  zoom: {
    label: 'Zoom Phone',
    abbr:  'ZM',
    bg:    '#1b2c40',
    color: '#2d8cff',
    badge: { background: '#1b3a6033', color: '#2d8cff', border: '1px solid #2d8cff44' },
  },
};

function PlatformBadge({ type = 'teams' }) {
  const p = PLATFORMS[type];
  return (
    <span style={{ ...p.badge, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500 }}>
      <span style={{
        width: 14, height: 14, borderRadius: 3, background: p.bg,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <span style={{ color: p.color, fontSize: 7, fontWeight: 900 }}>{p.abbr}</span>
      </span>
      {p.label}
    </span>
  );
}

function UserInitial({ name }) {
  const letters = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg, var(--brand-dark), var(--brand))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700, color: 'var(--text)',
    }}>{letters}</div>
  );
}

function AssignModal({ target, customerId, isAdmin, onClose, onAssign }) {
  const [dids,         setDids]         = useState([]);
  const [didsLoading,  setDidsLoading]  = useState(true);
  const [filterSite,   setFilterSite]   = useState('all');
  const [didSearch,    setDidSearch]    = useState('');
  const [selectedDid,  setSelectedDid]  = useState(null);
  const [useManual,    setUseManual]    = useState(false);
  const [manualNum,    setManualNum]    = useState('');
  const [removing,     setRemoving]     = useState(false);
  const [assigning,    setAssigning]    = useState(false);
  const [psCmd,        setPsCmd]        = useState('');
  const [err,          setErr]          = useState('');

  useEffect(() => {
    setDidsLoading(true);
    listDids()
      .then(data => {
        const all = data.dids || [];
        // Only numbers unassigned to any platform user, belonging to this customer
        setDids(all.filter(d =>
          !d.teamsUserId &&
          !d.zoomUserId &&
          (!customerId || String(d.customerId) === String(customerId))
        ));
      })
      .catch(() => {})
      .finally(() => setDidsLoading(false));
  }, [customerId]);

  const sites = useMemo(() => {
    const map = {};
    dids.forEach(d => { if (d.siteId && d.siteName) map[d.siteId] = d.siteName; });
    return Object.entries(map)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [dids]);

  const visibleDids = useMemo(() => {
    let list = dids;
    if (filterSite === 'none') list = dids.filter(d => !d.siteId);
    else if (filterSite !== 'all') list = dids.filter(d => String(d.siteId) === filterSite);
    if (didSearch.trim()) {
      const q = didSearch.toLowerCase();
      list = list.filter(d =>
        d.e164.includes(q) ||
        d.siteName?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [dids, filterSite, didSearch]);

  const effectiveNumber = removing ? '' : useManual ? manualNum.trim() : (selectedDid?.e164 || '');

  const reset = () => { setErr(''); setPsCmd(''); };

  const handle = async () => {
    setAssigning(true); reset();
    try {
      const res = await onAssign(target.msUserId, effectiveNumber, target.upn, target.phoneNumber);
      if (res.queued) {
        if (res.psCommand) {
          // Number assigned but VRP couldn't be auto-applied — keep modal open, show psCommand
          setPsCmd(res.psCommand);
          setErr(`✓ Number assigned. VRP '${res.vrpName}' must be set manually — run the PowerShell command below, then close:`);
        } else {
          onClose(removing ? null : effectiveNumber, res.jobId || null);
        }
      } else if (res.permissionMissing) {
        setPsCmd(res.psCommand || '');
        setErr(`Permission required: ${res.permissionName} — re-connect with Microsoft 365 or run the PowerShell command below`);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setAssigning(false);
    }
  };

  const canSubmit = !assigning && (removing || !!effectiveNumber);

  return (
    <div className="modal-overlay" onClick={() => onClose(null)}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{target.phoneNumber ? 'Change Phone Number' : 'Assign Phone Number'}</div>
            <div style={{ fontSize: 12, color: 'var(--muted-3)', marginTop: 2 }}>{target.displayName} · {target.upn}</div>
          </div>
          <button className="btn btn-secondary btn-xs" onClick={() => onClose(null)}><X size={12}/></button>
        </div>

        <div className="modal-body">
          {/* Current number banner */}
          {target.phoneNumber && (
            <div style={{ background: 'var(--border)', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'var(--muted-3)' }}>Current:</span>
              <span style={{ fontFamily: 'monospace', color: 'var(--ok)' }}>{target.phoneNumber}</span>
            </div>
          )}

          {removing ? (
            <div style={{ background: 'var(--err-bg)', border: '1px solid var(--err-border)', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: 'var(--err)' }}>
              This will remove <strong style={{ fontFamily: 'monospace' }}>{target.phoneNumber}</strong> from {target.displayName}.
              The number will return to the unassigned pool.
            </div>
          ) : !useManual ? (
            <>
              {/* Site filter */}
              {sites.length > 0 && (
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <MapPin size={11} color="var(--muted-3)"/> Filter by Site
                  </label>
                  <select
                    className="select-input"
                    value={filterSite}
                    onChange={e => { setFilterSite(e.target.value); setSelectedDid(null); }}
                  >
                    <option value="all">All sites ({dids.length} numbers)</option>
                    {sites.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({dids.filter(d => String(d.siteId) === s.id).length} numbers)
                      </option>
                    ))}
                    <option value="none">No site assigned ({dids.filter(d => !d.siteId).length} numbers)</option>
                  </select>
                </div>
              )}

              {/* Number search */}
              {visibleDids.length > 8 && (
                <div className="form-group">
                  <input
                    className="form-input font-mono"
                    placeholder="Search numbers…"
                    value={didSearch}
                    onChange={e => { setDidSearch(e.target.value); setSelectedDid(null); }}
                    style={{ fontSize: 12 }}
                  />
                </div>
              )}

              {/* DID picker list */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  Available Numbers
                  {!didsLoading && (
                    <span style={{ marginLeft: 6, color: 'var(--muted-3)', fontWeight: 400, fontSize: 11 }}>
                      {visibleDids.length} unassigned
                    </span>
                  )}
                </label>
                {didsLoading ? (
                  <div style={{ padding: 16, textAlign: 'center', color: 'var(--muted-3)', fontSize: 12 }}>
                    <RefreshCw size={13} style={{ animation: 'spin 0.8s linear infinite', marginRight: 6 }}/>
                    Loading numbers…
                  </div>
                ) : visibleDids.length === 0 ? (
                  <div style={{ padding: '12px 14px', background: 'var(--border)', borderRadius: 8, color: 'var(--muted-3)', fontSize: 12 }}>
                    No unassigned numbers{filterSite !== 'all' ? ' for this site' : ''}.
                    {dids.length === 0 && ' Numbers must be assigned to this customer in the Number Inventory.'}
                  </div>
                ) : (
                  <div style={{ border: '1px solid var(--border-strong)', borderRadius: 8, maxHeight: 240, overflowY: 'auto' }}>
                    {visibleDids.map((d, i) => {
                      const sel = selectedDid?.didId === d.didId;
                      return (
                        <div
                          key={d.didId}
                          onClick={() => { setSelectedDid(sel ? null : d); reset(); }}
                          style={{
                            padding: '9px 12px',
                            cursor: 'pointer',
                            borderBottom: i < visibleDids.length - 1 ? '1px solid var(--border)' : 'none',
                            background: sel ? 'var(--info-bg)' : 'transparent',
                            display: 'flex', alignItems: 'center', gap: 10,
                          }}
                          onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#1a1f30'; }}
                          onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}
                        >
                          {/* Radio dot */}
                          <div style={{
                            width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                            border: `2px solid ${sel ? 'var(--info)' : 'var(--border-strong)'}`,
                            background: sel ? 'var(--info)' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {sel && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--on-brand)' }}/>}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: 'monospace', fontSize: 13, color: sel ? 'var(--info)' : 'var(--text-2)' }}>{d.e164}</div>
                            {(d.siteName || d.description || d.country) && (
                              <div style={{ fontSize: 11, color: 'var(--muted-3)', marginTop: 1 }}>
                                {[d.siteName, d.description, d.country].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </div>
                          {sel && <Check size={14} color="var(--info)"/>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Manual entry */
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">E.164 Phone Number</label>
              <input
                className="form-input font-mono"
                placeholder="+441234567890"
                value={manualNum}
                onChange={e => { setManualNum(e.target.value); reset(); }}
                autoFocus
              />
              <div className="form-hint">Enter a number not shown in the list above (E.164 format).</div>
            </div>
          )}

          {/* Mode toggles */}
          {!removing && (
            <div style={{ marginTop: 10, display: 'flex', gap: 12 }}>
              <button
                onClick={() => { setUseManual(m => !m); setSelectedDid(null); setManualNum(''); reset(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-3)', fontSize: 11, padding: 0, textDecoration: 'underline' }}
              >
                {useManual ? '← Pick from list' : 'Enter number manually →'}
              </button>
            </div>
          )}

          {/* Error / PS fallback */}
          {err && <div style={{ fontSize: 12, color: err.startsWith('✓') ? 'var(--warn)' : 'var(--err)', marginTop: 10 }}>{err}</div>}
          {psCmd && (
            <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 10, marginTop: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{err?.startsWith('✓') ? 'Run in Teams PowerShell module:' : 'PowerShell fallback (run in Teams PowerShell module):'}</span>
                <button
                  onClick={() => navigator.clipboard.writeText(psCmd)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-3)', padding: 2, display: 'flex', alignItems: 'center', gap: 3 }}
                >
                  <Copy size={11}/><span style={{ fontSize: 10 }}>Copy</span>
                </button>
              </div>
              <code style={{ fontSize: 10, color: 'var(--cyan)', wordBreak: 'break-all' }}>{psCmd}</code>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          {/* Remove option (left side) */}
          <div>
            {target.phoneNumber && !removing && (
              <button
                className="btn btn-secondary btn-xs"
                onClick={() => { setRemoving(true); reset(); }}
                style={{ color: 'var(--err)', borderColor: '#991b1b55', fontSize: 11 }}
              >
                <X size={10}/> Remove number
              </button>
            )}
            {removing && (
              <button className="btn btn-secondary btn-xs" onClick={() => setRemoving(false)}>
                ← Back
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => onClose(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handle} disabled={!canSubmit}>
              {assigning
                ? <RefreshCw size={12} style={{ animation: 'spin 0.8s linear infinite' }}/>
                : removing ? <X size={12}/> : <Check size={12}/>
              }
              {removing ? 'Confirm Remove' : effectiveNumber ? 'Assign Number' : 'Select a number'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UsersPage({ user, viewAsCustomer }) {
  const [users,          setUsers]          = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [syncingTeams,   setSyncingTeams]   = useState(false);
  const [syncingZoom,    setSyncingZoom]    = useState(false);
  const [flash,          setFlash]          = useState(null);
  const [search,         setSearch]         = useState('');
  const [filterStatus,   setFilterStatus]   = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [assignTarget,   setAssignTarget]   = useState(null);
  const [vrpTarget,      setVrpTarget]      = useState(null); // user being VRP-edited
  const [vrps,           setVrps]           = useState([]);
  const [vrpSaving,      setVrpSaving]      = useState(false);
  const [vrpValue,       setVrpValue]       = useState('');
  const [vrpCmd,         setVrpCmd]         = useState('');

  const cid = viewAsCustomer?.id ?? (user?.isAdmin ? null : user?.id);

  const flashMsg = (type, text) => {
    setFlash({ type, text });
    setTimeout(() => setFlash(null), 5000);
  };

  const load = useCallback(async (silent = false) => {
    if (!cid) { setUsers([]); return; }
    if (!silent) setLoading(true);
    try {
      const [teamsRes, zoomRes, vrpRes] = await Promise.allSettled([
        getTeamsUsers(cid),
        getZoomUsers(cid),
        listVoiceRoutingPolicies(cid),
      ]);
      if (vrpRes.status === 'fulfilled') setVrps(vrpRes.value.policies || []);
      const teamsUsers = teamsRes.status === 'fulfilled'
        ? (teamsRes.value.users || []).map(u => ({ ...u, platform: 'teams' }))
        : [];
      const zoomUsers = zoomRes.status === 'fulfilled'
        ? (zoomRes.value.users || []).map(u => ({
            ...u,
            platform:       'zoom',
            msUserId:       u.zoomUserId,
            upn:            u.email,
            effectiveVrp:   u.callingPlan || '',
            accountEnabled: u.status === 'active',
          }))
        : [];
      setUsers([...teamsUsers, ...zoomUsers]);
    } catch (e) {
      if (!silent) flashMsg('error', e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [cid]);

  useEffect(() => { load(); }, [load]);

  // Teams VRP and number assignments land asynchronously (a sync or a backend
  // restart populates them a minute or so later). Silently re-pull so they
  // appear on their own — no manual refresh — paused while a modal or sync is
  // active so it can never disrupt what you're doing.
  useEffect(() => {
    if (!cid) return;
    const id = setInterval(() => {
      if (assignTarget || vrpTarget || syncingTeams || syncingZoom || loading) return;
      load(true);
    }, 20000);
    return () => clearInterval(id);
  }, [cid, assignTarget, vrpTarget, syncingTeams, syncingZoom, loading, load]);

  const handleSyncTeams = async () => {
    if (!cid) return;
    setSyncingTeams(true);
    try {
      const res = await syncTeamsUsers(cid);
      await load();
      flashMsg('success', `Synced ${res.synced} voice users from Microsoft 365${res.total && res.total !== res.synced ? ` (${res.total} total in tenant)` : ''}. Voice policies are syncing in the background — they'll appear here automatically in a moment.`);
    } catch (e) {
      flashMsg('error', `Teams sync failed: ${e.message}`);
    } finally {
      setSyncingTeams(false);
    }
  };

  const handleSyncZoom = async () => {
    if (!cid) return;
    setSyncingZoom(true);
    try {
      const res = await syncZoomUsers(cid);
      await load();
      flashMsg('success', `Synced ${res.synced} Zoom Phone users`);
    } catch (e) {
      flashMsg('error', `Zoom sync failed: ${e.message}`);
    } finally {
      setSyncingZoom(false);
    }
  };

  const handleAssign = async (userId, number, upn, currentPhone) => {
    if (assignTarget?.platform === 'zoom') {
      const res = await assignZoomNumber(userId, number, assignTarget.phoneNumberId, cid);
      return { queued: !!res.ok };
    }
    return assignTeamsNumber(userId, number, upn, currentPhone, cid);
  };

  const handleAssignClose = (newNumber, jobId) => {
    if (newNumber !== null && assignTarget) {
      const { msUserId, platform } = assignTarget;
      setUsers(prev => prev.map(u =>
        u.platform === platform && u.msUserId === msUserId
          ? { ...u, phoneNumber: newNumber || null }
          : u
      ));
      flashMsg('success', newNumber
        ? `${newNumber} assigned to ${assignTarget.displayName}`
        : `Phone number removed from ${assignTarget.displayName}`
      );
      if (jobId) {
        pollJob(jobId, job => {
          if (job.status === 'done') flashMsg('success', job.message || 'VRP applied in Teams');
          else flashMsg('error', `VRP Teams update failed: ${job.message || 'unknown error'}`);
        });
      }
    }
    setAssignTarget(null);
  };

  // Filtering
  const filtered = users.filter(u => {
    if (filterPlatform !== 'all' && u.platform !== filterPlatform) return false;
    if (filterStatus === 'assigned'   && !(u.phoneNumber || u.assignedE164)) return false;
    if (filterStatus === 'unassigned' && (u.phoneNumber || u.assignedE164))  return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      u.displayName?.toLowerCase().includes(q) ||
      u.upn?.toLowerCase().includes(q) ||
      u.phoneNumber?.includes(q) ||
      u.siteName?.toLowerCase().includes(q)
    );
  });

  const userSortAccessors = useMemo(() => ({
    phone:  u => u.phoneNumber || u.assignedE164 || '',
    site:   u => u.siteName || '',
    policy: u => u.vrpName || u.callingPlan || '',
    status: u => (u.phoneNumber || u.assignedE164) ? 0 : (u.hasPhoneLicense || u.accountEnabled) ? 1 : 2,
  }), []);
  const { sortKey: uSortKey, sortDir: uSortDir, handleSort: uHandleSort, sortedRows: sortedUsers } = useSortable(filtered, userSortAccessors);

  const withPhone  = users.filter(u => u.phoneNumber || u.assignedE164).length;
  const withDid    = users.filter(u => u.assignedE164).length;
  const teamsCount = users.filter(u => u.platform === 'teams').length;
  const zoomCount  = users.filter(u => u.platform === 'zoom').length;
  const noCustomer = user?.isAdmin && !cid;

  return (
    <div className="content">

      {/* Flash */}
      {flash && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 1000,
          padding: '10px 16px', borderRadius: 8, fontSize: 13,
          background: flash.type === 'success' ? 'var(--ok-bg)' : 'var(--err-btn-bg)',
          color:      flash.type === 'success' ? 'var(--ok)' : 'var(--err)',
          border:     `1px solid ${flash.type === 'success' ? '#16653444' : 'var(--err-border)'}`,
        }}>{flash.text}</div>
      )}

      {/* Customer context banner */}
      {viewAsCustomer && (
        <div className="alert alert-info" style={{ marginBottom: 16, borderColor: '#2563eb44', background: 'var(--info-bg)' }}>
          <Info size={14} style={{ flexShrink: 0, color: 'var(--info)' }}/>
          <span style={{ color: 'var(--info)' }}>
            Showing users for <strong style={{ color: 'var(--info)' }}>{viewAsCustomer.name}</strong>
          </span>
        </div>
      )}

      {/* No customer selected (admin only) */}
      {noCustomer && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', gap: 16 }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UserCheck size={32} color="var(--muted-3)"/>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--muted-2)' }}>Select a customer to view their users</div>
          <div style={{ fontSize: 13, color: 'var(--muted-3)', textAlign: 'center', maxWidth: 360 }}>
            Use the "View as customer" dropdown in the sidebar to choose a customer account.
            Users are scoped per customer.
          </div>
        </div>
      )}

      {/* Content when customer selected */}
      {!noCustomer && (
        <>
          {/* Stats */}
          <div className="stats-grid mb-20" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {[
              { label: 'Total Users',    value: loading ? '—' : users.length, color: 'var(--info)' },
              { label: 'With Phone',     value: loading ? '—' : withPhone,    color: 'var(--ok)' },
              { label: 'DID Linked',     value: loading ? '—' : withDid,      color: 'var(--purple)' },
              { label: 'Teams / Zoom',   value: loading ? '—' : `${teamsCount} / ${zoomCount}`, color: 'var(--warn)' },
            ].map(s => (
              <div key={s.label} className="stat-card">
                <div className="stat-label">{s.label}</div>
                <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">User Directory</div>
                <div className="card-sub">
                  All users across connected UC &amp; CC platforms{viewAsCustomer ? ` for ${viewAsCustomer.name}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
                  <RefreshCw size={13} style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined}/>
                  Refresh
                </button>
                <button className="btn btn-secondary btn-sm" onClick={handleSyncTeams} disabled={syncingTeams || loading}>
                  <RefreshCw size={13} style={syncingTeams ? { animation: 'spin 0.8s linear infinite' } : undefined}/>
                  {syncingTeams ? 'Syncing…' : 'Sync M365'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={handleSyncZoom} disabled={syncingZoom || loading}
                  style={{ color: '#2d8cff', borderColor: '#2d8cff44' }}>
                  <RefreshCw size={13} style={syncingZoom ? { animation: 'spin 0.8s linear infinite' } : undefined}/>
                  {syncingZoom ? 'Syncing…' : 'Sync Zoom'}
                </button>
              </div>
            </div>

            {/* Filters */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
                <Search size={14} color="var(--muted-3)"/>
                <input
                  placeholder="Search by name, email, or phone…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <select
                className="form-input select-input"
                style={{ fontSize: 12, padding: '5px 10px', width: 'auto' }}
                value={filterPlatform}
                onChange={e => setFilterPlatform(e.target.value)}
              >
                <option value="all">All platforms</option>
                <option value="teams">Microsoft Teams</option>
                <option value="zoom">Zoom Phone</option>
              </select>
              <select
                className="form-input select-input"
                style={{ fontSize: 12, padding: '5px 10px', width: 'auto' }}
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
              >
                <option value="all">All users</option>
                <option value="assigned">With phone number</option>
                <option value="unassigned">Without phone number</option>
              </select>
            </div>

            {/* Table */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--muted-3)' }}>
                <RefreshCw size={20} style={{ animation: 'spin 0.8s linear infinite', color: 'var(--muted-3)', marginBottom: 12 }}/>
                <div>Loading users…</div>
              </div>
            ) : users.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 56, gap: 12 }}>
                <UserX size={40} color="var(--border)"/>
                <div style={{ color: 'var(--muted-2)', fontSize: 14 }}>No users synced</div>
                <div style={{ color: 'var(--muted-3)', fontSize: 13, textAlign: 'center', maxWidth: 360 }}>
                  Connect a Microsoft Teams or Zoom Phone integration via UC/CC Platforms, then sync users above.
                </div>
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--muted-3)', fontSize: 13 }}>
                No users match your search.
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <SortTh label="User"          sortKey="displayName" currentKey={uSortKey} currentDir={uSortDir} onSort={uHandleSort} />
                      <SortTh label="Platform"      sortKey="platform"    currentKey={uSortKey} currentDir={uSortDir} onSort={uHandleSort} />
                      <SortTh label="Status"        sortKey="status"      currentKey={uSortKey} currentDir={uSortDir} onSort={uHandleSort} />
                      <SortTh label="Phone Number"  sortKey="phone"       currentKey={uSortKey} currentDir={uSortDir} onSort={uHandleSort} />
                      <SortTh label="Site"          sortKey="site"        currentKey={uSortKey} currentDir={uSortDir} onSort={uHandleSort} />
                      <SortTh label="Policy / Plan" sortKey="policy"      currentKey={uSortKey} currentDir={uSortDir} onSort={uHandleSort} />
                      <th style={{ width: 90 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map(u => (
                      <tr key={`${u.platform}-${u.msUserId || u.id}`}>
                        {/* User */}
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <UserInitial name={u.displayName}/>
                            <div>
                              <div style={{ fontWeight: 500, color: 'var(--text-2)', fontSize: 13 }}>{u.displayName}</div>
                              <div style={{ fontSize: 11, color: 'var(--muted-3)', fontFamily: 'monospace' }}>{u.upn}</div>
                            </div>
                          </div>
                        </td>

                        {/* Platform */}
                        <td><PlatformBadge type={u.platform}/></td>

                        {/* Status — 3-state: Active / Licensed / Disabled */}
                        <td>
                          {u.platform === 'teams' ? (
                            (u.phoneNumber || u.assignedE164)
                              ? <span className="badge badge-green"><span className="dot green"/>Active</span>
                              : u.hasPhoneLicense
                                ? <span className="badge badge-amber"><span className="dot amber"/>Licensed</span>
                                : <span className="badge badge-red"><span className="dot red"/>Disabled</span>
                          ) : (
                            (u.phoneNumber || u.assignedE164)
                              ? <span className="badge badge-green"><span className="dot green"/>Active</span>
                              : u.accountEnabled
                                ? <span className="badge badge-amber"><span className="dot amber"/>Licensed</span>
                                : <span className="badge badge-red"><span className="dot red"/>Disabled</span>
                          )}
                        </td>

                        {/* Phone number — Teams/Zoom value takes priority; fall back to portal DID link */}
                        <td>
                          {(u.phoneNumber || u.assignedE164)
                            ? (
                              <div>
                                <span style={{ fontFamily: 'monospace', color: 'var(--info)', fontSize: 12 }}>
                                  {u.phoneNumber || u.assignedE164}
                                </span>
                                {u.phoneNumber && u.assignedE164 && u.phoneNumber !== u.assignedE164 && (
                                  <div style={{ fontSize: 10, color: 'var(--muted-3)', marginTop: 2 }}>
                                    DID: {u.assignedE164}
                                  </div>
                                )}
                              </div>
                            )
                            : <span style={{ color: 'var(--muted-3)', fontSize: 12 }}>—</span>
                          }
                        </td>

                        {/* Site */}
                        <td>
                          {u.siteName
                            ? <span style={{ fontSize: 12, color: 'var(--muted)' }}>{u.siteName}</span>
                            : <span style={{ color: 'var(--muted-3)', fontSize: 12 }}>—</span>
                          }
                        </td>

                        {/* Voice Routing Policy */}
                        <td>
                          {u.platform === 'teams' ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                              {u.vrpName ? (
                                <span style={{
                                  fontFamily: 'monospace', fontSize: 11, color: 'var(--purple)',
                                  background: '#3b076433', border: '1px solid #6b21a833',
                                  padding: '2px 7px', borderRadius: 10,
                                }}>{u.vrpName}</span>
                              ) : (
                                <span style={{ color: 'var(--muted-3)', fontSize: 12 }}>—</span>
                              )}
                              <button
                                className="btn btn-secondary btn-xs"
                                style={{ padding: '1px 5px', fontSize: 10 }}
                                onClick={() => { setVrpTarget(u); setVrpValue(u.vrpName || u.effectiveVrp || ''); setVrpCmd(''); }}
                                title="Set VRP"
                              >VRP</button>
                            </div>
                          ) : (
                            (u.vrpName || u.effectiveVrp)
                              ? <span style={{
                                  fontFamily: 'monospace', fontSize: 11, color: 'var(--warn)',
                                  background: 'var(--warn-bg)', border: '1px solid #92400e33',
                                  padding: '2px 7px', borderRadius: 10,
                                }}>{u.vrpName || u.effectiveVrp}</span>
                              : <span style={{ color: 'var(--muted-3)', fontSize: 12 }}>—</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td>
                          <button
                            className="btn btn-secondary btn-xs"
                            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                            onClick={() => setAssignTarget(u)}
                          >
                            <Phone size={10}/>
                            {(u.phoneNumber || u.assignedE164) ? 'Change' : 'Assign'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer */}
            {!loading && filtered.length > 0 && (
              <div style={{ padding: '10px 20px', borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--muted-3)' }}>
                {filtered.length} of {users.length} user{users.length !== 1 ? 's' : ''}
                {teamsCount > 0 && ` · ${teamsCount} Teams`}
                {zoomCount > 0 && ` · ${zoomCount} Zoom`}
              </div>
            )}
          </div>
        </>
      )}

      {/* Assign modal */}
      {assignTarget && (
        <AssignModal
          target={assignTarget}
          customerId={cid}
          isAdmin={user?.isAdmin}
          onClose={handleAssignClose}
          onAssign={handleAssign}
        />
      )}

      {/* VRP modal */}
      {vrpTarget && (
        <div className="modal-overlay" onClick={() => setVrpTarget(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Set Voice Routing Policy</div>
                <div style={{ fontSize: 12, color: 'var(--muted-3)', marginTop: 2 }}>{vrpTarget.displayName} · {vrpTarget.upn}</div>
              </div>
              <button className="modal-close" onClick={() => setVrpTarget(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <select
                className="form-control"
                value={vrpValue}
                onChange={e => setVrpValue(e.target.value)}
                style={{ marginBottom: 12 }}
              >
                <option value="">— None (Global) —</option>
                {vrps.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
              {vrpCmd && (
                <div style={{ background: 'var(--bg)', borderRadius: 6, padding: '8px 10px', fontSize: 11, fontFamily: 'monospace', color: 'var(--muted)', whiteSpace: 'pre', marginBottom: 8 }}>{vrpCmd}</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setVrpTarget(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={vrpSaving}
                onClick={async () => {
                  setVrpSaving(true);
                  try {
                    const res = await setTeamsUserVrp(vrpTarget.msUserId, vrpValue, cid);
                    if (res.ok) {
                      setUsers(prev => prev.map(u =>
                        u.msUserId === vrpTarget.msUserId ? { ...u, vrpName: vrpValue || null } : u
                      ));
                      setVrpTarget(null);
                      flashMsg('success', `VRP saved for ${vrpTarget.displayName} — applying in Teams…`);
                      pollJob(res.jobId, job => {
                        if (job.status === 'done') flashMsg('success', job.message || 'VRP applied in Teams');
                        else flashMsg('error', `VRP Teams update failed: ${job.message || 'unknown error'}`);
                      });
                    }
                  } catch { flashMsg('error', 'Failed to update VRP'); }
                  finally { setVrpSaving(false); }
                }}
              >{vrpSaving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
